import Worker from "./worker";
import Logger from "./logger";
import { WorkerStatus } from "./utils";
import { TaskService, tasks } from "./taskService";
import { EventEmitter } from "eventemitter3";
import UploadProgressService from "./uploadProgressService";

export default class Workspace extends EventEmitter {
  constructor(options) {
    super();
    this.options = options;
    this.workers = [];
    this._taskService = null;
    this.isOnlne = navigator.onLine;
    this.maxWorkers = options.maxWorkers || 1;
    this._logger = new Logger({ debug: options.debug });
    this._progressService = null;
  }

  _getUploadProgressService() {
    if (this._progressService == null) {
      this._progressService = new UploadProgressService({
        debug: this.options.debug,
      });
      this._progressService.on("speed", (e) => {
        this.emit("UploadSpeed", e);
        this._logger.log("上传速度: ", e);
      });
      this._progressService.on("progress", (e) => {
        this.emit("UploadProgress", e);
        this._logger.log("上传进度: ", e);
      });
    }
    return this._progressService;
  }

  _getTaskService() {
    if (this._taskService == null) {
      this._taskService = new TaskService({
        logger: this._logger, 
      });
      this._taskService.on("NewTasks", (tasks) => {
        this._logger.log("新的上传任务: ", tasks);
        this.emit("NewTasks", tasks);
      });
      this._taskService.on("RemoveTask", (tid) => {
        this._logger.log("移除一个上传任务: ", tid);
        this.emit("RemoveTask", tid);
      });
      this._taskService.on("TaskStatusChange", (tid, status) => {
        this._logger.log("任务状态发生变更: ", tid, status); 
        this.emit("TaskStatusChange", tid, status);
      });
      this._taskService.on("FileStatusChange", (tid, fid, status) => {
        this._logger.log("任务文件状态发生变更: ", tid, status);
        this.emit("FileStatusChange", tid, fid, status);
      });
    }
    return this._taskService;
  }

  async networkChange(online) {
    this.isOnlne = online;
    this._logger.log("网络状态发生变更: ", online);
    const taskService = this._getTaskService();
    if (online) {
      for(const worker of this.workers) {
        const { tid, manual } = worker;
        if (tid == null) continue;
        if(manual) continue;
        taskService.resume(tid);
      }
      // 开始任务
      await this.start();
    } else {
      // 暂停所有任务 
      for (const worker of this.workers) {
        const { tid, status } = worker;
        if (tid == null)continue;
        if (status === WorkerStatus.IDLE) continue;
        worker.stop();
        taskService.pause(worker.tid);
      }
    }
  }

  async addFolderTask(items, pid, behavior) {
    const taskService = this._getTaskService();
    const ok = taskService.addFolder(items, pid, behavior);
    await this.start();
    return ok;
  }

  async addFilesTask(items, pid, behavior) {
    const taskService = this._getTaskService();
    const ok = taskService.addFiles(items, pid, behavior);
    await this.start();
    return ok;
  } 

  async start() {
    this._logger.log("[workspace] 准备开始一个任务");
    if (!this.isOnlne) {
      this._logger.log("[workspace] 无法开始任务，没有网络");
      return false;
    }
    const taskService = this._getTaskService();
    let worker = this.workers.find((t) => t.status === WorkerStatus.IDLE);
    if (worker == null) {
      if (this.workers.length >= this.maxWorkers) {
        this._logger.log("[workspace] 无法开始任务，已经达到最大 worker 数量");
        return false;
      }
      const options = {
        ...this.options,
        logger: this._logger,
        progresser: this._getUploadProgressService(),
      };
      worker = new Worker(this.options);
      worker.on("StatusChange", async (status) => {
        this._logger.log("[workspace] worker 状态变更: ", status);
        await this.start(); 
      });
      worker.on("BeginHash", (tid, fid) => {
        this._logger.log("[workspace] 开始计算 MD5: ", tid, fid);
        this.emit("BeginHash", tid, fid);
      });
      worker.on("EndHash", (tid, fid) => {
        this._logger.log("[workspace] 计算 MD5 完成: ", tid, fid);
        this.emit("EndHash", tid, fid);
      });
      worker.on("Hashing", (e) => {
        this._logger.log("计算MD5中: ", e);
        this.emit("Hashing", e);
      });
      worker.on("BeginPrepare", (tid, fid) => {
        this._logger.log("[workspace] 开始上传初始化: ", tid, fid);
        this.emit("BeginPrepare", tid, fid);
      });
      worker.on("Prepared", (tid, fid, data) => {
        taskService.beforeUpload(tid, fid, data);
        this._logger.log("[workspace] 上传初始化结果: ", data);
      });
      worker.on("EndPrepare", (tid, fid) => {
        this._logger.log("[workspace] 上传初始化结束: ", tid, fid);
        this.emit("EndPrepare", tid, fid);
      });
      worker.on("BeginCommitBlocks", (tid, fid) => {
        this._logger.log("[workspace] 开始计算要上传的分块: ", tid, fid);
        this.emit("BeginCommitBlocks", tid, fid);
      });
      worker.on("EndCommitBlocks", (tid, fid) => {
        this._logger.log("[workspace] 计算要上传的分块结束: ", tid, fid);
        this.emit("EndCommitBlocks", tid, fid);
      });
      worker.on("BeginUploadFile", (tid, fid) => {
        this._logger.log("[workspace] 开始上传文件: ", tid, fid);
        this.emit("BeginUploadFile", tid, fid);
      });
      worker.on("EndUploadFile", (tid, fid) => {
        this._logger.log("[workspace] 上传文件结束: ", tid, fid);
        this.emit("EndUploadFile", tid, fid);
      });
      worker.on("BeginMergeFile", (tid, fid) => {
        this._logger.log("[workspace] 开始合并文件: ", tid, fid);
        this.emit("BeginMergeFile", tid, fid);
      });
      worker.on("EndMergeFile", (tid, fid) => {
        this._logger.log("[workspace] 合并文件结束: ", tid, fid);
        this.emit("EndMergeFile", tid, fid);
      });
      worker.on("FileUpload", (tid, fid) => {
        const ok = taskService.afterUpload(tid, fid, (done) => {
          if (!done) return;
          const pargresser = this._getUploadProgressService();
          pargresser.clear(tid);
          const task = tasks.find((t) => t.tid === tid);
          this.emit("TaskComplete", task);
        });
        this._logger.log("[workspace] 任务是否完成: ", ok);
      });
      worker.on("UploadCommit", (args) => {
        const {tid, bytes, total} = args;
        const progresser = this._getUploadProgressService();
        progresser.commit(tid, bytes, total);
      });
      worker.on("UploadBytes", (args) => {
        const {tid, bytes, total} = args;
        const progresser = this._getUploadProgressService();
        progresser.execute(tid, bytes, total);
      });
      worker.on("TaskAbort", (tid) => {
        this._logger.log("[workspace] 任务被中止: ", tid);
        this.emit("TaskAbort", tid);
        const progresser = this._getUploadProgressService();
        progresser.clear(tid);
      });
      // 上传任务出错
      worker.on("error", ({ tid, fid, code, message }) => {
        // 设置当前任务下的文件上传失败
        taskService.error(tid, fid, code, message);
        // 暂停当前任务下的所有上传
        this.pause(tid);
        this.emit("error", { tid, fid, code, message });
        this._logger.error("[workspace] 上传出错: ", tid, fid, code, message);
      });
      // 开始任务
      this.workers.push(worker);
    } else {
      this._logger.log("[workspace] 获取到一个空闲的 worker");
    }
    const e = taskService.next();
    if (e == null) {
      this._logger.log("[workspace] 没有任务了");
      return;
    }
    const { uploadedBytes } = e;
    const { tid, size } = e.task; 
    const progresser = this._getUploadProgressService();
    progresser.commit(tid, uploadedBytes, size);
    this._logger.log(
      `[workspace] 获取到一个任务：${tid}，已提交 ${uploadedBytes}/${size} bytes`
    );
    await worker.start(e);
  }

  async pause(tid) {
    const worker = this.workers.find((t) => t.tid === tid);
    if (worker) worker.stop();
    const taskService = this._getTaskService();
    const ok = taskService.pause(tid);
    if (!ok) {
      this._logger.log(`[workspace] 暂停失败：任务 - ${tid} 不存在或者任务已经完成`);
    }
    await this.start();
  }

  async resume(tid) {
    const taskService = this._getTaskService();
    taskService.resume(tid);
    await this.start();
  }

  async remove(tid) {
    const worker = this.workers.find((t) => t.tid === tid);
    if (worker) worker.stop();
    const taskService = this._getTaskService();
    const aborts = taskService.remove(tid);
    if (aborts.length === 0) return
    for(const {fileId, uploadId} of aborts) {
      await worker.abortUploadFile(fileId, uploadId)
    } 
  }
}
